import React, { useState, useEffect, useCallback } from 'react';
import { useCustomClaims } from '../../hooks/useCustomClaims';
import LoadingSpinner from '../common/LoadingSpinner';

const STAFF_ROLES = ['admin', 'operator'];

/**
 * Composant de gestion des rôles utilisateurs (ADMIN SEULEMENT)
 * Utilise les Custom Claims Firebase pour une sécurité maximale
 */
const UserRoleManager = () => {
  const { 
    isAdmin, 
    listUsersWithRoles, 
    setUserRole, 
    revokeUserTokens,
    saveOperatorTelegramConfig,
    deleteOperatorTelegramConfig,
    loading: claimsLoading 
  } = useCustomClaims();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('staff');
  const [pageToken, setPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [telegramForms, setTelegramForms] = useState({});

  // Charger la liste des utilisateurs
  const loadUsers = useCallback(async (reset = false) => {
    if (!isAdmin()) {
      setError('Accès refusé : seuls les administrateurs peuvent gérer les rôles');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const token = reset ? '' : (pageToken || '');
      const result = await listUsersWithRoles(100, token);
      
      if (reset) {
        setUsers(result.users);
      } else {
        setUsers(prev => [...prev, ...result.users]);
      }
      
      setPageToken(result.pageToken);
      setHasMore(result.hasMore);
      
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      setError(error.message || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, listUsersWithRoles, pageToken]);

  // Charger les utilisateurs au montage
  useEffect(() => {
    if (!claimsLoading) {
      loadUsers(true);
    }
  }, [claimsLoading, loadUsers]);

  useEffect(() => {
    setTelegramForms((previousForms) => {
      const nextForms = { ...previousForms };

      users.forEach((user) => {
        if (user.role !== 'operator') {
          return;
        }

        nextForms[user.uid] = {
          botToken: previousForms[user.uid]?.botToken || '',
          chatId: previousForms[user.uid]?.chatId || '',
          enabled: typeof previousForms[user.uid]?.enabled === 'boolean'
            ? previousForms[user.uid].enabled
            : user.operatorTelegram?.enabled !== false,
        };
      });

      return nextForms;
    });
  }, [users]);

  // Changer le rôle d'un utilisateur
  const handleRoleChange = async (userId, newRole, userEmail) => {
    const actionKey = `role_${userId}`;
    
    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      setError(null);
      
      // Confirmation pour les actions critiques
      if (newRole === null) {
        const confirmed = window.confirm(
          `Êtes-vous sûr de vouloir supprimer le rôle de ${userEmail} ?`
        );
        if (!confirmed) return;
      } else if (newRole === 'admin') {
        const confirmed = window.confirm(
          `Êtes-vous sûr de vouloir promouvoir ${userEmail} en tant qu'administrateur ?\n\nCette action lui donnera tous les privilèges administrateur.`
        );
        if (!confirmed) return;
      }
      
      await setUserRole(userId, newRole);
      
      // Mettre à jour la liste locale
      setUsers(prev => prev.map(user => 
        user.uid === userId 
          ? { ...user, role: newRole }
          : user
      ));
      
      // Afficher un message de succès
      const roleText = newRole ? `rôle ${newRole}` : 'rôle supprimé';
      alert(`✅ ${roleText} attribué avec succès à ${userEmail}`);
      
    } catch (error) {
      console.error('Erreur lors du changement de rôle:', error);
      setError(error.message || 'Erreur lors du changement de rôle');
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  // Révoquer les tokens d'un utilisateur
  const handleRevokeTokens = async (userId, userEmail) => {
    const actionKey = `revoke_${userId}`;
    
    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir révoquer tous les tokens de ${userEmail} ?\n\nCette action forcera l'utilisateur à se reconnecter.`
    );
    
    if (!confirmed) return;
    
    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      setError(null);
      
      await revokeUserTokens(userId);
      alert(`✅ Tokens révoqués avec succès pour ${userEmail}`);
      
    } catch (error) {
      console.error('Erreur lors de la révocation des tokens:', error);
      setError(error.message || 'Erreur lors de la révocation des tokens');
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleTelegramFieldChange = (userId, field, value) => {
    setTelegramForms((previousForms) => ({
      ...previousForms,
      [userId]: {
        botToken: previousForms[userId]?.botToken || '',
        chatId: previousForms[userId]?.chatId || '',
        enabled: typeof previousForms[userId]?.enabled === 'boolean'
          ? previousForms[userId].enabled
          : true,
        [field]: value,
      },
    }));
  };

  const handleSaveTelegramConfig = async (user) => {
    const actionKey = `telegram_save_${user.uid}`;
    const currentForm = telegramForms[user.uid] || { botToken: '', chatId: '', enabled: true };

    try {
      setActionLoading((previous) => ({ ...previous, [actionKey]: true }));
      setError(null);

      const result = await saveOperatorTelegramConfig(user.uid, {
        botToken: currentForm.botToken,
        chatId: currentForm.chatId,
        enabled: currentForm.enabled,
      });

      setUsers((previousUsers) => previousUsers.map((entry) => (
        entry.uid === user.uid
          ? { ...entry, operatorTelegram: result.config || null }
          : entry
      )));

      setTelegramForms((previousForms) => ({
        ...previousForms,
        [user.uid]: {
          botToken: '',
          chatId: previousForms[user.uid]?.chatId || '',
          enabled: typeof previousForms[user.uid]?.enabled === 'boolean'
            ? previousForms[user.uid].enabled
            : true,
        },
      }));

      alert(`✅ Telegram operateur enregistre pour ${user.email}`);
    } catch (saveError) {
      console.error('Erreur lors de la sauvegarde Telegram operateur:', saveError);
      setError(saveError.message || 'Erreur lors de la sauvegarde Telegram operateur');
      alert(`❌ Erreur: ${saveError.message}`);
    } finally {
      setActionLoading((previous) => ({ ...previous, [actionKey]: false }));
    }
  };

  const handleDeleteTelegramConfig = async (user) => {
    const confirmed = window.confirm(
      `Supprimer la configuration Telegram de ${user.email} ?`
    );

    if (!confirmed) {
      return;
    }

    const actionKey = `telegram_delete_${user.uid}`;

    try {
      setActionLoading((previous) => ({ ...previous, [actionKey]: true }));
      setError(null);

      await deleteOperatorTelegramConfig(user.uid);

      setUsers((previousUsers) => previousUsers.map((entry) => (
        entry.uid === user.uid
          ? { ...entry, operatorTelegram: null }
          : entry
      )));

      setTelegramForms((previousForms) => ({
        ...previousForms,
        [user.uid]: {
          botToken: '',
          chatId: '',
          enabled: true,
        },
      }));

      alert(`✅ Configuration Telegram supprimee pour ${user.email}`);
    } catch (deleteError) {
      console.error('Erreur lors de la suppression Telegram operateur:', deleteError);
      setError(deleteError.message || 'Erreur lors de la suppression Telegram operateur');
      alert(`❌ Erreur: ${deleteError.message}`);
    } finally {
      setActionLoading((previous) => ({ ...previous, [actionKey]: false }));
    }
  };

  // Filtrer les utilisateurs
  const filteredUsers = users.filter(user => {
    const normalizedRole = user.role || null;
    const isStaffRole = STAFF_ROLES.includes(normalizedRole);

    const matchesSearch = !searchTerm || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.uid.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = (
      (roleFilter === 'staff' && isStaffRole) ||
      (roleFilter === 'eligible' && !isStaffRole) ||
      normalizedRole === roleFilter
    );
    
    return matchesSearch && matchesRole;
  });

  // Composant de sélection de rôle
  const RoleSelector = ({ user }) => {
    const actionKey = `role_${user.uid}`;
    const isLoading = actionLoading[actionKey];
    const selectedRole = STAFF_ROLES.includes(user.role) ? user.role : '';
    
    return (
      <div className="flex items-center space-x-2">
        <select
          value={selectedRole}
          onChange={(e) => {
            const newRole = e.target.value || null;
            handleRoleChange(user.uid, newRole, user.email);
          }}
          disabled={isLoading}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <option value="">{selectedRole ? "Retirer l'acces staff" : 'Attribuer un role staff'}</option>
          <option value="operator">Operateur</option>
          <option value="admin">Administrateur</option>
        </select>
        
        {isLoading && (
          <div className="w-4 h-4">
            <LoadingSpinner size="sm" />
          </div>
        )}
      </div>
    );
  };

  // Composant de badge de rôle
  const RoleBadge = ({ role }) => {
    const styles = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      reseller: 'bg-blue-100 text-blue-800 border-blue-200',
      operator: 'bg-amber-100 text-amber-800 border-amber-200',
      none: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    
    const displayRole = role || 'none';
    const displayText = {
      admin: 'Admin',
      reseller: 'Revendeur',
      operator: 'Operateur',
      none: 'Aucun'
    }[displayRole];
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[displayRole]}`}>
        {displayText}
      </span>
    );
  };

  const OperatorTelegramConfig = ({ user }) => {
    if (user.role !== 'operator') {
      return <span className="text-xs text-gray-400">Reserve aux operateurs</span>;
    }

    const currentForm = telegramForms[user.uid] || {
      botToken: '',
      chatId: '',
      enabled: user.operatorTelegram?.enabled !== false,
    };
    const isSaving = Boolean(actionLoading[`telegram_save_${user.uid}`]);
    const isDeleting = Boolean(actionLoading[`telegram_delete_${user.uid}`]);
    const isConfigured = Boolean(user.operatorTelegram?.configured);

    return (
      <div className="min-w-[280px] space-y-3">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between gap-3">
            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
              isConfigured ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'
            }`}>
              {isConfigured ? 'Configure' : 'Non configure'}
            </span>
            {user.operatorTelegram?.updatedAt ? (
              <span className="text-[11px] text-gray-500">
                Maj {new Date(user.operatorTelegram.updatedAt).toLocaleDateString('fr-FR')}
              </span>
            ) : null}
          </div>

          <div className="mt-2 space-y-1 text-xs text-gray-600">
            <p>Bot: {user.operatorTelegram?.botTokenMasked || 'Aucun token enregistre'}</p>
            <p>Chat ID: {user.operatorTelegram?.chatIdMasked || 'Aucun chat ID enregistre'}</p>
          </div>
        </div>

        <div className="space-y-2">
          <input
            type="password"
            value={currentForm.botToken}
            onChange={(e) => handleTelegramFieldChange(user.uid, 'botToken', e.target.value)}
            placeholder={isConfigured ? 'Laisser vide pour conserver le token actuel' : 'Token BotFather'}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            value={currentForm.chatId}
            onChange={(e) => handleTelegramFieldChange(user.uid, 'chatId', e.target.value)}
            placeholder={isConfigured ? 'Nouveau chat ID ou laisser vide pour conserver' : 'Chat ID Telegram'}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
            <input
              type="checkbox"
              checked={Boolean(currentForm.enabled)}
              onChange={(e) => handleTelegramFieldChange(user.uid, 'enabled', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Notifications Telegram actives pour cet operateur
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleSaveTelegramConfig(user)}
            disabled={isSaving}
            className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>

          {isConfigured ? (
            <button
              type="button"
              onClick={() => handleDeleteTelegramConfig(user)}
              disabled={isDeleting}
              className="inline-flex items-center rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {isDeleting ? 'Suppression...' : 'Supprimer'}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  if (claimsLoading || loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
        <span className="ml-3 text-gray-600">Chargement des utilisateurs...</span>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 mb-2">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-red-800 mb-2">Accès refusé</h3>
        <p className="text-red-600">Seuls les administrateurs peuvent gérer les rôles utilisateurs.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* En-tête */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Gestion des rôles utilisateurs</h2>
            <p className="text-sm text-gray-600 mt-1">
              Gérez les rôles et permissions des utilisateurs via Firebase Custom Claims
            </p>
          </div>
          
          <button
            onClick={() => loadUsers(true)}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Actualiser</span>
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Rechercher par email ou ID utilisateur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="staff">Admins et operateurs</option>
              <option value="eligible">Comptes a promouvoir</option>
              <option value="operator">Operateurs</option>
              <option value="admin">Administrateurs</option>
            </select>
            
            <span className="text-sm text-gray-600">
              {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Liste des utilisateurs */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Utilisateur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rôle actuel
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Modifier le rôle
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Telegram operateur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => {
              const revokeActionKey = `revoke_${user.uid}`;
              const isRevokingTokens = actionLoading[revokeActionKey];
              
              return (
                <tr key={user.uid} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.email || 'Email non disponible'}
                      </div>
                      <div className="text-sm text-gray-500 font-mono">
                        {user.uid}
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <RoleBadge role={user.role} />
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <RoleSelector user={user} />
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.disabled 
                          ? 'bg-red-100 text-red-800' 
                          : user.emailVerified 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.disabled ? 'Désactivé' : user.emailVerified ? 'Vérifié' : 'Non vérifié'}
                      </span>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 align-top">
                    <OperatorTelegramConfig user={user} />
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleRevokeTokens(user.uid, user.email)}
                      disabled={isRevokingTokens}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50 flex items-center space-x-1"
                      title="Révoquer tous les tokens (forcer la reconnexion)"
                    >
                      {isRevokingTokens ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                        </svg>
                      )}
                      <span>Révoquer tokens</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {hasMore && (
        <div className="px-6 py-4 border-t border-gray-200 text-center">
          <button
            onClick={() => loadUsers(false)}
            disabled={loading}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
          >
            {loading ? 'Chargement...' : 'Charger plus'}
          </button>
        </div>
      )}

      {/* Message si aucun utilisateur */}
      {filteredUsers.length === 0 && !loading && (
        <div className="px-6 py-8 text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p>Aucun utilisateur trouvé avec les critères de recherche actuels.</p>
        </div>
      )}
    </div>
  );
};

export default UserRoleManager;
